import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import type { FeeBasis, HoaiCalculation, OfferReference as Ref } from '@pst/shared';
import type { ConnectionsView, GenesisInput, ImportDocument, MailInput, NtfyInput, PortalId, PortalInput, PortalJobsOverview, ThemeColors } from '@pst/shared';
import type { CurrentUser, HealthResponse, ImportCosts, ImportTendering, ImportTime, LoginRequest, ModuleInfo, RoleName, TaskStatus, Constellation as OfferConstellation, OfferParty as Party, OfferTexts as Texts, OfferVariantData as VariantData } from '@pst/shared';

/**
 * Thin fetch wrapper. Same-origin in production, proxied by Vite in
 * development, so no base URL and no CORS handling are needed.
 *
 * `credentials: 'same-origin'` carries the session cookie; there is no token
 * in browser storage by design (see ADR 0001).
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    const message = await response
      .json()
      .then((body: { message?: string }) => body.message)
      .catch(() => undefined);
    throw new ApiError(response.status, message ?? `HTTP ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  // An empty body is a legitimate answer - "no active baseline", for one -
  // and must not blow up as invalid JSON.
  const text = await response.text();
  return (text.length === 0 ? null : JSON.parse(text)) as T;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface Project {
  id: string;
  number: string;
  name: string;
  /** Where the project is built, e.g. "Berlin, Stadt" (ADR 0007). */
  location: string | null;
  archivedAt: string | null;
  /** Sent back with a change; a stale one is refused with 409. */
  version: number;
}

export interface UserSummary {
  id: string;
  email: string;
  displayName: string;
  nickname: string | null;
  role: RoleName;
  disabledAt: string | null;
  createdAt: string;
  version: number;
}

export interface OpenInvite {
  id: string;
  email: string;
  displayName: string | null;
  role: RoleName | null;
  projectNumber: string | null;
  projectName: string | null;
  createdAt: string;
  expiresAt: string;
}

/** A one-time link. Shown once: the server keeps only a hash of it. */
export interface IssuedLink {
  id: string;
  link: string;
  expiresAt: string;
  /** True when PST also sent it by e-mail. */
  mailed?: boolean;
}

export interface ProjectMember {
  userId: string;
  email: string;
  displayName: string;
  role: RoleName;
  addedAt: string;
}

export interface TokenInfo {
  kind: 'invite' | 'reset';
  email: string;
  displayName: string;
}

export const api = {
  health: () => request<HealthResponse>('/health'),
  me: () => request<CurrentUser>('/auth/me'),
  /** Passkey sign-in: options first, then the device's answer (AP-24). */
  passkeyLoginOptions: () => request<PublicKeyCredentialRequestOptionsJSON>('/auth/passkeys/login/options', { method: 'POST' }),
  passkeyLogin: (response: AuthenticationResponseJSON, remember: boolean) =>
    request<CurrentUser>('/auth/passkeys/login', { method: 'POST', body: JSON.stringify({ response, remember }) }),
  passkeys: () => request<Passkey[]>('/auth/passkeys'),
  passkeyRegisterOptions: () => request<PublicKeyCredentialCreationOptionsJSON>('/auth/passkeys/register/options', { method: 'POST' }),
  passkeyRegister: (response: RegistrationResponseJSON, name: string) =>
    request<Passkey>('/auth/passkeys/register', { method: 'POST', body: JSON.stringify({ response, name }) }),
  renamePasskey: (id: string, name: string) => request<void>(`/auth/passkeys/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  removePasskey: (id: string) => request<void>(`/auth/passkeys/${id}`, { method: 'DELETE' }),
  login: (body: LoginRequest) =>
    request<CurrentUser>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  logoutEverywhere: () =>
    request<{ endedSessions: number }>('/auth/logout-everywhere', { method: 'POST' }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    request<{ revokedSessions: number }>('/auth/password', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  modules: () => request<ModuleInfo[]>('/modules'),
  projects: () => request<Project[]>('/projects'),

  // --- Benutzer und Projektmitglieder ------------------------------------
  users: () => request<UserSummary[]>('/users'),
  updateProfile: (body: { displayName?: string; nickname?: string | null; ownerCodes?: string | null }) =>
    request<CurrentUser>('/auth/me', { method: 'PATCH', body: JSON.stringify(body) }),
  /** Only a deactivated account; `confirm` repeats its e-mail address. */
  removeUser: (id: string, confirm: string) =>
    request<void>(`/users/${id}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  updateUser: (id: string, body: { displayName?: string; nickname?: string | null; role?: RoleName; disabled?: boolean; version?: number }) =>
    request<void>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetLink: (id: string, sendMail = false) =>
    request<IssuedLink>(`/users/${id}/reset-link`, { method: 'POST', body: JSON.stringify({ sendMail }) }),
  mailStatus: () => request<{ configured: boolean }>('/users/mail'),
  mailTest: () => request<void>('/users/mail/test', { method: 'POST' }),
  invites: () => request<OpenInvite[]>('/users/invites'),
  invite: (body: { email: string; displayName: string; nickname: string | null; role: RoleName; projectId: string | null; sendMail?: boolean }) =>
    request<IssuedLink>('/users/invites', { method: 'POST', body: JSON.stringify(body) }),
  revokeInvite: (id: string) => request<void>(`/users/invites/${id}`, { method: 'DELETE' }),
  resendInvite: (id: string) => request<IssuedLink>(`/users/invites/${id}/resend`, { method: 'POST' }),

  members: (projectId: string) => request<ProjectMember[]>(`/projects/${projectId}/members`),
  memberCandidates: (projectId: string) =>
    request<Omit<ProjectMember, 'addedAt'>[]>(`/projects/${projectId}/members/candidates`),
  addMember: (projectId: string, userId: string) =>
    request<void>(`/projects/${projectId}/members`, { method: 'POST', body: JSON.stringify({ userId }) }),
  removeMember: (projectId: string, userId: string) =>
    request<void>(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' }),

  // Public: the page behind an invitation or password link.
  inspectToken: (token: string) =>
    request<TokenInfo>('/auth/account/inspect', { method: 'POST', body: JSON.stringify({ token }) }),
  redeemToken: (token: string, password: string) =>
    request<{ email: string }>('/auth/account/redeem', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
};

// --- Terminplanung (Modul 1) ---------------------------------------------

export interface PlanView {
  id: string;
  projectId: string;
  name: string;
  startDate: string;
  targetFinish: string | null;
  calculatedAt: string | null;
  calendar: {
    id: string;
    name: string;
    bundesland: string;
    workingWeekdays: number[];
    fronleichnam: boolean;
    mariaeHimmelfahrt: boolean;
    exceptions: { day: string; kind: string; note: string | null }[];
  };
}

export interface WbsNode {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  sortOrder: number;
  color: string | null;
}

/** A phase, with the span rolled up from everything below it. */
export interface GroupView {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  color: string | null;
  depth: number;
  earlyStart: string | null;
  earlyFinish: string | null;
  durationDays: number;
  critical: boolean;
  taskCount: number;
}

export type ConstraintType = 'startNoEarlierThan' | 'finishNoEarlierThan';

export interface SchedTask {
  id: string;
  wbsNodeId: string | null;
  code: string;
  name: string;
  durationDays: number;
  sortOrder: number;
  note: string | null;
  color: string | null;
  status: 'open' | 'running' | 'done' | 'blocked';
  owner: string | null;
  position: string | null;
  deliverable: string | null;
  deadline: string | null;
  constraintType: ConstraintType | null;
  constraintDate: string | null;
  earlyStart: string | null;
  earlyFinish: string | null;
  lateStart: string | null;
  lateFinish: string | null;
  totalFloat: number | null;
  freeFloat: number | null;
  critical: boolean | null;
  /** The summary it is shown in on the simplified plan (Zusammenfassung). */
  summaryId: string | null;
  /** When it was done (set on ticking off). */
  doneOn: string | null;
}

/** A change Claude proposes from new information (checked by the server). */
export type RollClaudeChange =
  | { type: 'duration'; code: string; days: number; reason: string }
  | { type: 'start'; code: string; date: string; reason: string }
  | { type: 'new'; code: string; name: string; days: number; after: string[]; phase: string | null; reason: string }
  | { type: 'link'; from: string; to: string; reason: string };

/** Rolling the plan forward: Claude's proposal from new information. */
export interface RollforwardStatus {
  available: boolean;
  pending: boolean;
  startedAt: string | null;
  info: string | null;
  changes: RollClaudeChange[] | null;
  note: string | null;
}

export interface RollforwardApply {
  statusDate: string;
  baselineName: string;
  updates: { taskId: string; startDate?: string | null; durationDays?: number }[];
  newTasks: { code: string; name: string; durationDays: number; wbsNodeId: string | null; after: string[]; startDate: string }[];
  links: { from: string; to: string }[];
}

/** Claude making the simplified plan (ADR 0016). */
export interface SummaryAiStatus {
  available: boolean;
  reason: string | null;
  pending: boolean;
  startedAt: string | null;
  note: string | null;
}

/** A summary of the simplified plan: several tasks as one bar. */
export interface ScheduleSummary {
  id: string;
  name: string;
}

export type DependencyType = 'FS' | 'SS' | 'FF' | 'SF';

export interface SchedDependency {
  id: string;
  predecessorId: string;
  successorId: string;
  type: DependencyType;
  lagDays: number;
}

export interface ScheduleView {
  projectStart: string;
  earliestFinish: string;
  targetFinish: string;
  behindTarget: boolean;
  criticalTaskIds: string[];
  calculatedAt: string;
  tasks: SchedTask[];
  groups: GroupView[];
  summaries: ScheduleSummary[];
  stale: boolean;
}

export interface BaselineSummary {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  taskCount: number;
}

export interface BaselineEntry {
  taskId: string | null;
  code: string;
  name: string;
  start: string;
  finish: string;
  durationDays: number;
}

export interface ActiveBaseline {
  id: string;
  name: string;
  createdAt: string;
  entries: BaselineEntry[];
}

const scheduling = (projectId: string) => `/scheduling/projects/${projectId}`;

/** A comment on a task (AP-108). */
export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string | null;
  authorName: string;
  body: string;
  createdAt: string;
  editedAt: string | null;
  mine: boolean;
  canDelete: boolean;
}

export const commentApi = {
  counts: (p: string) => request<Record<string, number>>(`${scheduling(p)}/comments/counts`),
  list: (p: string, taskId: string) => request<TaskComment[]>(`${scheduling(p)}/tasks/${taskId}/comments`),
  create: (p: string, taskId: string, body: string) => request<TaskComment>(`${scheduling(p)}/tasks/${taskId}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  update: (p: string, id: string, body: string) => request<TaskComment>(`${scheduling(p)}/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ body }) }),
  remove: (p: string, id: string) => request<void>(`${scheduling(p)}/comments/${id}`, { method: 'DELETE' }),
};

export const scheduleApi = {
  plan: (p: string) => request<PlanView>(`${scheduling(p)}/plan`),
  updatePlan: (p: string, body: Partial<Pick<PlanView, 'name' | 'startDate' | 'targetFinish'>>) =>
    request<PlanView>(`${scheduling(p)}/plan`, { method: 'PUT', body: JSON.stringify(body) }),
  updateCalendar: (p: string, body: Record<string, unknown>) =>
    request<PlanView>(`${scheduling(p)}/calendar`, { method: 'PUT', body: JSON.stringify(body) }),
  setException: (p: string, body: { day: string; kind: 'off' | 'on'; note: string | null }) =>
    request<PlanView>(`${scheduling(p)}/calendar/exceptions`, { method: 'PUT', body: JSON.stringify(body) }),
  removeException: (p: string, day: string) =>
    request<PlanView>(`${scheduling(p)}/calendar/exceptions/${day}`, { method: 'DELETE' }),

  templates: () =>
    request<{ key: string; title: string; description: string; count: number }[]>(
      '/scheduling/templates',
    ),
  applyTemplate: (p: string, key: string) =>
    request<WbsNode[]>(`${scheduling(p)}/templates/${key}`, { method: 'POST' }),

  wbs: (p: string) => request<WbsNode[]>(`${scheduling(p)}/wbs`),
  createWbs: (p: string, body: Record<string, unknown>) =>
    request<WbsNode>(`${scheduling(p)}/wbs`, { method: 'POST', body: JSON.stringify(body) }),
  updateWbs: (p: string, id: string, body: Record<string, unknown>) =>
    request<WbsNode>(`${scheduling(p)}/wbs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteWbs: (p: string, id: string) =>
    request<void>(`${scheduling(p)}/wbs/${id}`, { method: 'DELETE' }),

  tasks: (p: string) => request<SchedTask[]>(`${scheduling(p)}/tasks`),
  createTask: (p: string, body: Record<string, unknown>) =>
    request<SchedTask>(`${scheduling(p)}/tasks`, { method: 'POST', body: JSON.stringify(body) }),
  updateTask: (p: string, id: string, body: Record<string, unknown>) =>
    request<SchedTask>(`${scheduling(p)}/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTask: (p: string, id: string) =>
    request<void>(`${scheduling(p)}/tasks/${id}`, { method: 'DELETE' }),

  summaries: (p: string) => request<ScheduleSummary[]>(`${scheduling(p)}/summaries`),
  summaryAi: (p: string) => request<SummaryAiStatus>(`${scheduling(p)}/summaries/ai`),
  rollforward: (p: string) => request<RollforwardStatus>(`${scheduling(p)}/rollforward`),
  askRollforward: (p: string, body: { info: string; statusDate: string }) =>
    request<RollforwardStatus>(`${scheduling(p)}/rollforward/ask`, { method: 'POST', body: JSON.stringify(body) }),
  discardRollforward: (p: string) => request<void>(`${scheduling(p)}/rollforward/proposal`, { method: 'DELETE' }),
  applyRollforward: (p: string, body: RollforwardApply) =>
    request<{ baseline: string; updated: number; created: number; linked: number }>(`${scheduling(p)}/rollforward`, { method: 'POST', body: JSON.stringify(body) }),
  requestSummaryAi: (p: string) => request<SummaryAiStatus>(`${scheduling(p)}/summaries/ai`, { method: 'POST' }),
  createSummary: (p: string, body: { name: string; taskIds: string[] }) =>
    request<ScheduleSummary>(`${scheduling(p)}/summaries`, { method: 'POST', body: JSON.stringify(body) }),
  updateSummary: (p: string, id: string, body: { name?: string; taskIds?: string[] }) =>
    request<ScheduleSummary>(`${scheduling(p)}/summaries/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSummary: (p: string, id: string) =>
    request<void>(`${scheduling(p)}/summaries/${id}`, { method: 'DELETE' }),

  dependencies: (p: string) => request<SchedDependency[]>(`${scheduling(p)}/dependencies`),
  createDependency: (p: string, body: Record<string, unknown>) =>
    request<SchedDependency>(`${scheduling(p)}/dependencies`, { method: 'POST', body: JSON.stringify(body) }),
  deleteDependency: (p: string, id: string) =>
    request<void>(`${scheduling(p)}/dependencies/${id}`, { method: 'DELETE' }),

  baselines: (p: string) => request<BaselineSummary[]>(`${scheduling(p)}/baselines`),
  activeBaseline: (p: string) =>
    request<ActiveBaseline | null>(`${scheduling(p)}/baselines/active`),
  createBaseline: (p: string, name: string) =>
    request<BaselineSummary>(`${scheduling(p)}/baselines`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  activateBaseline: (p: string, id: string) =>
    request<void>(`${scheduling(p)}/baselines/${id}/active`, { method: 'PUT' }),
  deactivateBaseline: (p: string) =>
    request<void>(`${scheduling(p)}/baselines/active`, { method: 'DELETE' }),
  deleteBaseline: (p: string, id: string) =>
    request<void>(`${scheduling(p)}/baselines/${id}`, { method: 'DELETE' }),

  schedule: (p: string) => request<ScheduleView>(`${scheduling(p)}/schedule`),
  calculate: (p: string) =>
    request<ScheduleView>(`${scheduling(p)}/calculate`, { method: 'POST' }),
};

export const projectApi = {
  create: (body: { number: string; name: string }) =>
    request<Project>('/projects', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { number?: string; name?: string; location?: string | null; version?: number }) =>
    request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** `confirm` must repeat the project number. */
  remove: (id: string, confirm: string) =>
    request<void>(`/projects/${id}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
};

export interface TemplateSummary {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
}

export const templateApi = {
  list: () => request<TemplateSummary[]>('/templates'),
  get: (id: string) => request<TemplateSummary & { document: unknown }>(`/templates/${id}`),
  create: (body: { name: string; description: string | null; document: object }) =>
    request<TemplateSummary>('/templates', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: string) => request<void>(`/templates/${id}`, { method: 'DELETE' }),
};

// --- Projektstamm (Modul 2) ----------------------------------------------

export interface ProjectDetails {
  shortName: string | null;
  reference: string | null;
  description: string | null;
  client: string | null;
  offerDate: string | null;
  offerNet: number | null;
  offerGross: number | null;
  bindingUntil: string | null;
  hardDeadline: string | null;
  planVersion: string | null;
  facts: { label: string; value: string }[];
  assumptions: { marker: 'A' | '!' | '?' | null; text: string }[];
  risks: { code: string | null; title: string; measure: string | null }[];
  escalations: string[];
  weeks: { label: string; kw: number | null; from: string; to: string; topic: string | null }[];
  /** 0 while there is no master data yet; sent back with a change. */
  version: number;
}

export interface Contact {
  id: string;
  code: string | null;
  role: string | null;
  organisation: string | null;
  name: string | null;
  function: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  note: string | null;
  sortOrder: number;
}

export type ContactInput = Partial<Omit<Contact, 'id'>>;

const projectData = (projectId: string) => `/projectdata/projects/${projectId}`;

export interface TimeCategory {
  id: string;
  code: string;
  label: string | null;
  budgetHours: number | null;
  unpaid: boolean;
  sortOrder: number;
  booked: number;
  remaining: number | null;
  share: number | null;
  /** Hours the caller booked here. */
  mine: number;
  /** Billing groundwork (AP-107), project management only; null for others. */
  fee: number | null;
  progress: number | null;
  billed: number | null;
}

export interface TimeEntry {
  id: string;
  /** null: booked "Ohne Kategorie". */
  categoryId: string | null;
  userId: string | null;
  person: string;
  date: string;
  hours: number;
  taskCode: string | null;
  comment: string | null;
  mine: boolean;
}

export interface TimeSheet {
  categories: TimeCategory[];
  entries: TimeEntry[];
  /** Hours booked without a category, all and the caller's own. */
  uncategorized: { booked: number; mine: number };
  seesAll: boolean;
  total: number;
}

export type TimeEntryInput = Partial<Pick<TimeEntry, 'categoryId' | 'date' | 'hours' | 'taskCode' | 'comment'>>;
export type TimeCategoryInput = Partial<Pick<TimeCategory, 'code' | 'label' | 'budgetHours' | 'unpaid' | 'sortOrder' | 'fee' | 'progress' | 'billed'>>;

const time = (projectId: string) => `${projectData(projectId)}/time`;

export const timeApi = {
  get: (p: string) => request<TimeSheet>(time(p)),
  addEntry: (p: string, body: TimeEntryInput) => request<{ id: string }>(`${time(p)}/entries`, { method: 'POST', body: JSON.stringify(body) }),
  updateEntry: (p: string, id: string, body: TimeEntryInput) => request<void>(`${time(p)}/entries/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeEntry: (p: string, id: string) => request<void>(`${time(p)}/entries/${id}`, { method: 'DELETE' }),
  addCategory: (p: string, body: TimeCategoryInput) => request<{ id: string }>(`${time(p)}/categories`, { method: 'POST', body: JSON.stringify(body) }),
  workStages: (p: string) => request<{ added: number }>(`${time(p)}/categories/work-stages`, { method: 'POST' }),
  updateCategory: (p: string, id: string, body: TimeCategoryInput) => request<void>(`${time(p)}/categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeCategory: (p: string, id: string) => request<void>(`${time(p)}/categories/${id}`, { method: 'DELETE' }),
  csvUrl: (p: string) => `/api${time(p)}/export/csv`,
  feesUrl: (p: string) => `/api${time(p)}/export/fees`,
  /** Across projects (AP-107): hours by month, person, project; fee statement of all projects. */
  monthsUrl: (from: string | null, to: string | null) => `/api/projectdata/overview/time/export/months?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString()}`,
  allFeesUrl: () => '/api/projectdata/overview/time/export/fees',
};

export const projectDataApi = {
  get: (p: string) => request<{ details: ProjectDetails; contacts: Contact[] }>(projectData(p)),
  updateDetails: (p: string, body: Partial<ProjectDetails>) =>
    request<ProjectDetails>(`${projectData(p)}/details`, { method: 'PATCH', body: JSON.stringify(body) }),
  addContact: (p: string, body: ContactInput) =>
    request<Contact>(`${projectData(p)}/contacts`, { method: 'POST', body: JSON.stringify(body) }),
  updateContact: (p: string, id: string, body: ContactInput) =>
    request<Contact>(`${projectData(p)}/contacts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeContact: (p: string, id: string) =>
    request<void>(`${projectData(p)}/contacts/${id}`, { method: 'DELETE' }),
  // Loosely typed: the body is the project part of a PST-Import file, checked by the server.
  import: (p: string, body: { details: object; contacts: object[] }) =>
    request<{ contacts: number }>(`${projectData(p)}/import`, { method: 'POST', body: JSON.stringify(body) }),
};

// --- Import (PST-Import v1) ------------------------------------------------

export interface ImportSummary {
  groups: number;
  tasks: number;
  milestones: number;
  dependencies: number;
  overlaps: number;
  projectStart: string;
  earliestFinish: string;
  targetFinish: string | null;
  notices: string[];
}

export const importApi = {
  /** An MSPDI file (Microsoft Project XML) as the schedule part of a PST-Import document (AP-16). */
  readMspdi: (xml: string) => request<{ title: string; schedule: ImportDocument['schedule']; notes: string[] }>('/scheduling/import/mspdi', { method: 'POST', body: JSON.stringify({ xml }) }),
  previewSchedule: (schedule: unknown) =>
    request<ImportSummary>('/scheduling/import/preview', { method: 'POST', body: JSON.stringify(schedule) }),
  importSchedule: (projectId: string, schedule: unknown, replace = false) =>
    request<ImportSummary>(`/scheduling/projects/${projectId}/import${replace ? '?replace=true' : ''}`, {
      method: 'POST',
      body: JSON.stringify(schedule),
    }),
};

// --- Kostenplanung (Modul 3) ---------------------------------------------

export type CostStage = 'frame' | 'estimate' | 'calculation' | 'quote' | 'final';

export interface CostGroup {
  id: string;
  code: string;
  name: string;
  note: string | null;
  own: Record<CostStage, number | null>;
}

export interface CostTotals {
  code: string;
  parent: string | null;
  depth: number;
  stages: Record<CostStage, number | null>;
  committed: number;
}

export type CostFlag = 'A' | '?' | '!';
export type CostValueKind = 'quantity' | 'rate' | 'ratio';

export interface CostItem {
  id: string;
  groupId: string;
  stage: CostStage;
  /** Only in this variant; null = in all. */
  variantId: string | null;
  description: string;
  quantityFormula: string | null;
  takeoff: { text: string; formula: string }[] | null;
  unit: string | null;
  unitPriceFormula: string | null;
  flag: CostFlag | null;
  source: string | null;
  benchmarkId: string | null;
  note: string | null;
  sortOrder: number;
}

export interface CostValue {
  id: string;
  name: string;
  label: string | null;
  kind: CostValueKind;
  formula: string;
  perVariant: Record<string, string>;
  unit: string | null;
  note: string | null;
}

export interface CostVariant {
  id: string;
  code: string;
  name: string;
  description: string | null;
  riskPercent: number;
  rangePercent: number;
  isPrimary: boolean;
}

export interface CostLineResult {
  quantity: number | null;
  unitPrice: number | null;
  amount: number | null;
  error: string | null;
  takeoff: { value: number | null; error: string | null }[] | null;
}

export interface CostStageTotals {
  net: number | null;
  risk: number | null;
  withRisk: number | null;
  low: number | null;
  high: number | null;
}

export interface CostRollup {
  groups: CostTotals[];
  total: Record<CostStage, number | null>;
  committedTotal: number;
  unassignedCommitted: number;
}

export interface CostVariantResult {
  variantId: string;
  values: Record<string, { value: number | null; error: string | null }>;
  ratios: Record<CostStage, Record<string, { value: number | null; error: string | null }>>;
  lines: Record<string, CostLineResult>;
  rollup: CostRollup;
  totals: Record<CostStage, CostStageTotals>;
}

export type CostItemInput = Partial<Omit<CostItem, 'id' | 'groupId' | 'stage' | 'sortOrder'>>;
export type CostValueInput = Partial<Omit<CostValue, 'id' | 'perVariant'>> & { perVariant?: Record<string, string | null> };
export type CostVariantInput = Partial<Omit<CostVariant, 'id' | 'isPrimary'>> & { isPrimary?: true };

export interface CostPlan {
  vatRate: number;
  referenceStage: CostStage;
  /** Building price index used to convert catalog values (AP-80). */
  priceIndexType: PriceIndexType;
  riskPercent: number;
  rangePercent: number;
  variants: CostVariant[];
  primaryVariantId: string;
  groups: CostGroup[];
  items: CostItem[];
  values: CostValue[];
  results: CostVariantResult[];
  rollup: CostRollup;
  commitments: { source: string; sourceId: string; costGroup: string | null; label: string; amountNet: number; contractor: string | null; date: string | null }[];
}

export interface Benchmark {
  id: string;
  source: string;
  edition: string | null;
  priceDate: string | null;
  region: string | null;
  buildingType: string | null;
  costGroup: string | null;
  label: string;
  unit: string | null;
  low: number | null;
  mid: number | null;
  high: number | null;
  license: string | null;
  note: string | null;
  createdAt: string;
  /** Long text and execution time from the catalogue's texts (AP-88). */
  description: string | null;
  duration: string | null;
}
export type BenchmarkInput = Omit<Benchmark, 'id' | 'createdAt'>;

const costs = (projectId: string) => `/costs/projects/${projectId}`;

const variantQuery = (variantId?: string): string => (variantId ? `?variant=${encodeURIComponent(variantId)}` : '');

export const costsApi = {
  get: (p: string) => request<CostPlan>(costs(p)),
  updatePlan: (p: string, body: { vatRate?: number; referenceStage?: CostStage; riskPercent?: number; rangePercent?: number; priceIndexType?: PriceIndexType }) =>
    request<void>(costs(p), { method: 'PATCH', body: JSON.stringify(body) }),
  firstLevel: (p: string) => request<{ added: number }>(`${costs(p)}/first-level`, { method: 'POST' }),
  outline: (p: string, depth: 1 | 2 | 3) => request<{ added: number }>(`${costs(p)}/outline`, { method: 'POST', body: JSON.stringify({ depth }) }),
  addGroup: (p: string, body: { code: string; name: string }) =>
    request<{ id: string }>(`${costs(p)}/groups`, { method: 'POST', body: JSON.stringify(body) }),
  updateGroup: (p: string, id: string, body: Partial<{ code: string; name: string; note: string | null } & Record<CostStage, number | null>>) =>
    request<void>(`${costs(p)}/groups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeGroup: (p: string, id: string) => request<void>(`${costs(p)}/groups/${id}`, { method: 'DELETE' }),
  csvUrl: (p: string, variantId?: string) => `/api${costs(p)}/export/csv${variantQuery(variantId)}`,
  stageCsvUrl: (p: string, stage: CostStage, variantId?: string) => `/api${costs(p)}/export/stage/${stage}${variantQuery(variantId)}`,
  compareCsvUrl: (p: string, stage: CostStage) => `/api${costs(p)}/export/compare/${stage}`,
  addItem: (p: string, groupId: string, stage: CostStage, body: CostItemInput) =>
    request<{ id: string }>(`${costs(p)}/groups/${groupId}/items/${stage}`, { method: 'POST', body: JSON.stringify(body) }),
  updateItem: (p: string, id: string, body: CostItemInput) =>
    request<void>(`${costs(p)}/items/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeItem: (p: string, id: string) => request<void>(`${costs(p)}/items/${id}`, { method: 'DELETE' }),
  copyStage: (p: string, from: CostStage, to: CostStage) =>
    request<{ copied: number }>(`${costs(p)}/copy-stage`, { method: 'POST', body: JSON.stringify({ from, to }) }),
  addValue: (p: string, body: CostValueInput & { name: string; formula: string }) =>
    request<{ id: string }>(`${costs(p)}/values`, { method: 'POST', body: JSON.stringify(body) }),
  updateValue: (p: string, id: string, body: CostValueInput) =>
    request<void>(`${costs(p)}/values/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeValue: (p: string, id: string) => request<void>(`${costs(p)}/values/${id}`, { method: 'DELETE' }),
  addVariant: (p: string, body: CostVariantInput & { code: string; name: string; copyFrom?: string | null }) =>
    request<{ id: string }>(`${costs(p)}/variants`, { method: 'POST', body: JSON.stringify(body) }),
  updateVariant: (p: string, id: string, body: CostVariantInput) =>
    request<void>(`${costs(p)}/variants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeVariant: (p: string, id: string) => request<void>(`${costs(p)}/variants/${id}`, { method: 'DELETE' }),
};

/** An example object of a building type, with photo (AP-88). */
export interface BenchmarkObject {
  objectNo: string;
  edition: string;
  buildingType: string;
  title: string;
  description: string | null;
  facts: Record<string, string | number>;
  planner: string | null;
  credit: string | null;
  image: string | null;
  page: number | null;
}

/** What reading example objects in did; numbers another edition holds are left out (AP-129). */
export interface ObjectsImportResult {
  objects: number;
  withImage: number;
  missingImages: number;
  conflicts: { objectNo: string; edition: string }[];
  duplicates: number;
}

export const objectsApi = {
  types: () => request<{ buildingType: string; objects: number }[]>('/costs/benchmarks/objects/types'),
  list: (buildingType: string) => request<BenchmarkObject[]>(`/costs/benchmarks/objects?buildingType=${encodeURIComponent(buildingType)}`),
  imageUrl: (objectNo: string) => `/api/costs/benchmarks/objects/${encodeURIComponent(objectNo)}/image`,
  import: (objects: BenchmarkObject[]) => request<ObjectsImportResult>('/costs/benchmarks/objects', { method: 'PUT', body: JSON.stringify({ objects }) }),
};

export const benchmarkApi = {
  me: () => request<{ allowed: boolean }>('/costs/benchmarks/me'),
  list: () => request<Benchmark[]>('/costs/benchmarks'),
  add: (rows: BenchmarkInput[]) => request<{ added: number }>('/costs/benchmarks', { method: 'POST', body: JSON.stringify({ rows }) }),
  update: (id: string, body: Partial<BenchmarkInput>) => request<void>(`/costs/benchmarks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (ids: string[]) => request<{ removed: number }>('/costs/benchmarks/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  access: () => request<{ userIds: string[] }>('/costs/benchmarks/access'),
  setAccess: (userId: string, granted: boolean) => request<void>(`/costs/benchmarks/access/${userId}`, { method: granted ? 'PUT' : 'DELETE' }),
  importTexts: (csv: string) => request<{ texts: number; matched: number }>('/costs/benchmarks/texts', { method: 'PUT', body: JSON.stringify({ csv }) }),
};

// --- Eingangsordner (ADR 0009) ----------------------------------------------

export interface InboxSettings {
  enabled: boolean;
  notifyEmail: string | null;
  receiptDays: number;
}
export interface InboxStatus {
  settings: InboxSettings;
  inbox: { path: string; readable: boolean };
  outbox: { path: string; writable: boolean };
  mailConfigured: boolean;
  folders: { folder: string; label: string; extensions: string[] }[];
  lastScan: string | null;
  waiting: string[];
  unassigned: { path: string; reason: string }[];
  recent: { id: string; at: string; folder: string; fileName: string; outcome: 'ok' | 'skipped' | 'failed'; summary: string; details: string | null; receipt: string | null; mailed: boolean }[];
}
export const inboxApi = {
  status: () => request<InboxStatus>('/inbox'),
  save: (body: Partial<InboxSettings>) => request<void>('/inbox/settings', { method: 'PUT', body: JSON.stringify(body) }),
  scan: () => request<{ handled: number }>('/inbox/scan', { method: 'POST' }),
};

// --- Systemprotokoll (Einstellungen) -----------------------------------------

export type LogLevel = 'error' | 'warn' | 'log' | 'debug';

export interface LogLine {
  id: number;
  at: string;
  level: LogLevel;
  context: string | null;
  message: string;
  trace: string | null;
}

export interface LogPage {
  entries: LogLine[];
  next: number | null;
  lastDay: Record<LogLevel, number>;
  retentionDays: number;
}

export const systemLogApi = {
  page: (filter: { levels: readonly LogLevel[]; q: string; before?: number | null }) => {
    const p = new URLSearchParams({ limit: '200' });
    if (filter.levels.length) p.set('levels', filter.levels.join(','));
    if (filter.q.trim()) p.set('q', filter.q.trim());
    if (filter.before) p.set('before', String(filter.before));
    return request<LogPage>(`/system-log?${p.toString()}`);
  },
};

// --- Regionalfaktoren und Baupreisindex (AP-80) -----------------------------

/** A series of the building price index: a building type, "Außenanlagen", "Instandhaltung …". */
export type PriceIndexType = string;

export interface RegionalFactor {
  id: string;
  source: string;
  edition: string | null;
  area: string;
  district: string;
  factor: number;
  license: string | null;
}
export type RegionalFactorInput = Omit<RegionalFactor, 'id'>;

export interface PriceIndex {
  id: string;
  /** The series (building type, Außenanlagen, Instandhaltung …). */
  buildingType: PriceIndexType;
  /** Kind of work; the table's total line is "insgesamt". */
  work: string;
  vat: 'mit' | 'ohne';
  /** JJJJ-Qn, or JJJJ from the annual tables */
  period: string;
  value: number;
  base: string | null;
  source: string | null;
  /** Destatis' mark next to the value, as delivered. */
  flag: string | null;
  updatedAt: string;
}
export type PriceIndexInput = Omit<PriceIndex, 'id' | 'updatedAt' | 'work' | 'vat' | 'flag'> & Partial<Pick<PriceIndex, 'work' | 'vat' | 'flag'>>;

const ref = '/costs/reference';
/** The office's DIN 276 outline (AP-85). */
export interface DinGroup {
  code: string;
  name: string;
  note: string | null;
  level: 1 | 2 | 3;
  page: number | null;
  edition: string;
}

/** An entry of the DIN 277 (AP-109): a term, an area of the outline, or a rule. */
export interface Din277Entry {
  position: number;
  section: string;
  abbreviation: string | null;
  name: string;
  parent: string | null;
  text: string;
  kind: 'begriff' | 'gliederung' | 'regel';
  page: number | null;
  edition: string;
}

export const din277Api = {
  list: () => request<Din277Entry[]>('/costs/reference/din/277'),
  import: (csv: string, edition: string) => request<{ entries: number }>('/costs/reference/din/277', { method: 'PUT', body: JSON.stringify({ csv, edition }) }),
};

export const dinApi = {
  list: () => request<DinGroup[]>('/costs/reference/din'),
  import: (csv: string, edition: string) => request<{ groups: number }>('/costs/reference/din', { method: 'PUT', body: JSON.stringify({ csv, edition }) }),
};

export const referenceApi = {
  factors: () => request<RegionalFactor[]>(`${ref}/regional-factors`),
  addFactors: (rows: RegionalFactorInput[], replace: boolean) =>
    request<{ added: number }>(`${ref}/regional-factors`, { method: 'POST', body: JSON.stringify({ rows, replace }) }),
  removeFactors: (ids: string[]) => request<{ removed: number }>(`${ref}/regional-factors/delete`, { method: 'POST', body: JSON.stringify({ ids }) }),
  indices: () => request<PriceIndex[]>(`${ref}/price-indices`),
  saveIndices: (rows: PriceIndexInput[]) => request<{ saved: number }>(`${ref}/price-indices`, { method: 'PUT', body: JSON.stringify({ rows }) }),
  importDestatis: (fileName: string, content: string) =>
    request<{ table: string; title: string; saved: number; periods: string[]; declined: string | null }>(`${ref}/price-indices/import-destatis`, { method: 'POST', body: JSON.stringify({ fileName, content }) }),
  removeIndices: (ids: string[]) => request<{ removed: number }>(`${ref}/price-indices/delete`, { method: 'POST', body: JSON.stringify({ ids }) }),
  /** GENESIS-Online of Destatis (AP-83): set up? and fetch now. */
  genesisStatus: () => request<{ configured: boolean }>(`${ref}/price-indices/genesis`),
  fetchGenesis: () => request<{ table: string; saved: number; periods: string[]; error: string | null }[]>(`${ref}/price-indices/genesis`, { method: 'POST' }),
};

// --- Ausschreibung und Vergabe (Modul 6) ------------------------------------

export type PositionType = 'normal' | 'lump' | 'contingency' | 'alternative';
export type PackageStatus = 'draft' | 'sent' | 'offers' | 'awarded' | 'cancelled';
export type BidderStatus = 'invited' | 'submitted' | 'declined' | 'excluded';

export interface PackageSummary {
  id: string;
  code: string;
  name: string;
  costGroup: string | null;
  procedure: string;
  status: PackageStatus;
  deadline: string | null;
  positions: number;
  bidders: number;
  submitted: number;
  lowest: { company: string; amount: number } | null;
  awarded: { company: string; amount: number } | null;
  estimate: number | null;
}

export interface TenderPackage {
  id: string;
  code: string;
  name: string;
  costGroup: string | null;
  procedure: string;
  status: PackageStatus;
  vatRate: number;
  sentOn: string | null;
  deadline: string | null;
  bindingUntil: string | null;
  workStart: string | null;
  workEnd: string | null;
  description: string | null;
  awardedBidderId: string | null;
  awardedOn: string | null;
  awardNote: string | null;
}

export interface TenderItem {
  id: string;
  parentId: string | null;
  /** note = Vorbemerkung (AP-105): no OZ, quantity or price. */
  kind: 'title' | 'position' | 'note';
  oz: string;
  shortText: string;
  longText: string | null;
  quantity: number | null;
  unit: string | null;
  positionType: PositionType;
  estimateUnitPrice: number | null;
  /** Set when copied from a text library. */
  origin: string | null;
  sortOrder: number;
}

export interface Bidder {
  id: string;
  company: string;
  contact: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  status: BidderStatus;
  discountPercent: number;
  skontoPercent: number;
  skontoDays: number | null;
  submittedOn: string | null;
  note: string | null;
  sortOrder: number;
}

export interface BidderResult {
  bidderId: string;
  net: number;
  discount: number;
  netAfterDiscount: number;
  vat: number;
  gross: number;
  skonto: number;
  missing: number;
  rank: number | null;
  aboveLowestPercent: number | null;
  againstEstimatePercent: number | null;
}

export interface PackageDetail {
  package: TenderPackage;
  items: TenderItem[];
  bidders: Bidder[];
  evaluation: {
    items: {
      itemId: string;
      unitPrices: Record<string, number | null>;
      totals: Record<string, number | null>;
      estimateTotal: number | null;
      lowestUnitPrice: number | null;
      highestUnitPrice: number | null;
      meanUnitPrice: number | null;
      countsForTotal: boolean;
    }[];
    bidders: BidderResult[];
    titleTotals: Record<string, Record<string, number>>;
    estimateTotal: number | null;
    lowestBidderId: string | null;
  };
}

export interface ItemInput {
  kind: 'title' | 'position' | 'note';
  oz: string;
  shortText: string;
  longText?: string | null;
  quantity?: number | null;
  unit?: string | null;
  positionType?: PositionType;
  estimateUnitPrice?: number | null;
}

const tender = (projectId: string) => `/tendering/projects/${projectId}/packages`;

/** Each module's own section of a PST-Import file (ADR 0002: every module imports its own part). */
export const exchangeApi = {
  costs: (p: string) => request<ImportCosts>(`${costs(p)}/exchange`),
  replaceCosts: (p: string, body: ImportCosts) => request<{ groups: number; values: number; items: number; variants: number }>(`${costs(p)}/exchange`, { method: 'PUT', body: JSON.stringify(body) }),
  tendering: (p: string) => request<ImportTendering>(`/tendering/projects/${p}/exchange`),
  replaceTendering: (p: string, body: ImportTendering) => request<{ packages: number }>(`/tendering/projects/${p}/exchange`, { method: 'PUT', body: JSON.stringify(body) }),
  time: (p: string, entries: boolean) => request<ImportTime>(`${time(p)}/exchange${entries ? '?entries=1' : ''}`),
  replaceTime: (p: string, body: ImportTime) => request<{ categories: number; entries: number }>(`${time(p)}/exchange`, { method: 'PUT', body: JSON.stringify(body) }),
};

/** A supplement (Nachtrag) to an awarded package (AP-106). */
export interface Supplement {
  id: string;
  number: string;
  title: string;
  reason: string;
  status: 'angemeldet' | 'angeboten' | 'geprueft' | 'beauftragt' | 'abgelehnt';
  submittedOn: string | null;
  offeredNet: number | null;
  checkedNet: number | null;
  approvedNet: number | null;
  approvedOn: string | null;
  note: string;
}

export const tenderApi = {
  list: (p: string) => request<PackageSummary[]>(tender(p)),
  create: (p: string, body: { name: string; costGroup?: string | null; procedure?: string }) =>
    request<{ id: string }>(tender(p), { method: 'POST', body: JSON.stringify(body) }),
  sample: (p: string) => request<{ id: string }>(`${tender(p)}/sample`, { method: 'POST' }),
  get: (p: string, id: string) => request<PackageDetail>(`${tender(p)}/${id}`),
  update: (p: string, id: string, body: Partial<Omit<TenderPackage, 'id' | 'awardedBidderId' | 'awardedOn' | 'awardNote'>>) =>
    request<void>(`${tender(p)}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (p: string, id: string) => request<void>(`${tender(p)}/${id}`, { method: 'DELETE' }),
  duplicate: (p: string, id: string) => request<{ id: string }>(`${tender(p)}/${id}/duplicate`, { method: 'POST' }),
  addItem: (p: string, id: string, body: ItemInput & { afterId?: string | null; parentId?: string | null }) =>
    request<void>(`${tender(p)}/${id}/items`, { method: 'POST', body: JSON.stringify(body) }),
  importItems: (p: string, id: string, rows: ItemInput[], replace: boolean) =>
    request<{ imported: number }>(`${tender(p)}/${id}/items/import`, { method: 'POST', body: JSON.stringify({ rows, replace }) }),
  updateItem: (p: string, id: string, itemId: string, body: Partial<ItemInput>) =>
    request<void>(`${tender(p)}/${id}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeItem: (p: string, id: string, itemId: string) => request<void>(`${tender(p)}/${id}/items/${itemId}`, { method: 'DELETE' }),
  addBidder: (p: string, id: string, body: { company: string; contact?: string | null; email?: string | null }) =>
    request<void>(`${tender(p)}/${id}/bidders`, { method: 'POST', body: JSON.stringify(body) }),
  updateBidder: (p: string, id: string, bidderId: string, body: Partial<Omit<Bidder, 'id' | 'sortOrder'>>) =>
    request<void>(`${tender(p)}/${id}/bidders/${bidderId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeBidder: (p: string, id: string, bidderId: string) => request<void>(`${tender(p)}/${id}/bidders/${bidderId}`, { method: 'DELETE' }),
  setPrices: (p: string, id: string, bidderId: string, prices: { itemId: string; unitPrice: number | null }[]) =>
    request<void>(`${tender(p)}/${id}/bidders/${bidderId}/prices`, { method: 'PUT', body: JSON.stringify({ prices }) }),
  award: (p: string, id: string, body: { bidderId: string; awardedOn: string; note: string | null }) =>
    request<void>(`${tender(p)}/${id}/award`, { method: 'POST', body: JSON.stringify(body) }),
  withdrawAward: (p: string, id: string) => request<void>(`${tender(p)}/${id}/award`, { method: 'DELETE' }),
  supplements: (p: string, id: string) => request<Supplement[]>(`${tender(p)}/${id}/supplements`),
  addSupplement: (p: string, id: string, body: Omit<Supplement, 'id'>) => request<{ id: string }>(`${tender(p)}/${id}/supplements`, { method: 'POST', body: JSON.stringify(body) }),
  saveSupplement: (p: string, id: string, sid: string, body: Omit<Supplement, 'id'>) => request<void>(`${tender(p)}/${id}/supplements/${sid}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeSupplement: (p: string, id: string, sid: string) => request<void>(`${tender(p)}/${id}/supplements/${sid}`, { method: 'DELETE' }),
  lvCsvUrl: (p: string, id: string) => `/api${tender(p)}/${id}/export/lv`,
  /** The LV as GAEB DA XML X83 (Angebotsaufforderung, AP-105). */
  lvGaebUrl: (p: string, id: string) => `/api${tender(p)}/${id}/export/gaeb`,
  comparisonCsvUrl: (p: string, id: string) => `/api${tender(p)}/${id}/export/preisspiegel`,
  importGaebIntoLv: (p: string, id: string, body: { fileName: string; contentBase64: string; targetTitleId: string | null; alsoLibrary: boolean }) =>
    request<{ copied: number; notes: number; report: string[]; library: { outcome: string; name: string | null; reason: string } | null }>(`${tender(p)}/${id}/items/from-gaeb`, { method: 'POST', body: JSON.stringify(body) }),
  copyFromLibrary: (p: string, id: string, body: { libraryId: string; itemIds: string[]; targetTitleId: string | null }) =>
    request<{ copied: number }>(`${tender(p)}/${id}/items/from-library`, { method: 'POST', body: JSON.stringify(body) }),
};

// --- Textbibliotheken (Modul Vergabe, büroweit) -------------------------------

export interface LibrarySummary {
  id: string;
  name: string;
  description: string | null;
  titles: number;
  positions: number;
  notes: number;
  updatedAt: string;
  /** Why this library may duplicate another (AP-81), until cleared. */
  warning: string | null;
  sourceName: string | null;
  issued: string | null;
}

export interface LibraryItem {
  id: string;
  /** note: a preliminary remark ("Vorbemerkung"), kept in the library, not copied into an LV. */
  kind: 'title' | 'position' | 'note';
  /** The source's OZ when imported from GAEB. */
  oz: string | null;
  parentId: string | null;
  shortText: string;
  longText: string | null;
  unit: string | null;
  /** Kind of position, copied into the LV with it (AP-116); 'normal' for titles and notes. */
  positionType: PositionType;
  source: string | null;
  attribution: string | null;
  sortOrder: number;
}

export interface LibraryDetail {
  id: string;
  name: string;
  description: string | null;
  /** Report of GAEB imports, newest first. */
  importLog: string | null;
  items: LibraryItem[];
}

export type LibraryItemInput = Partial<Pick<LibraryItem, 'shortText' | 'longText' | 'unit' | 'positionType' | 'source' | 'attribution'>>;

export type ImportAction = 'auto' | 'create' | 'replace' | 'append' | 'skip';
export type ImportVerdict = 'identical' | 'newer' | 'older' | 'similar' | 'new';
export interface ImportResult {
  outcome: 'created' | 'replaced' | 'appended' | 'skipped' | 'preview';
  verdict: ImportVerdict;
  reason: string;
  match: { id: string; name: string; share: number } | null;
  action: Exclude<ImportAction, 'auto'>;
  libraryId: string | null;
  libraryName: string | null;
  report: string[];
  name: string;
  issued: string | null;
}
export interface ImportRun {
  id: string;
  at: string;
  trigger: 'manual' | 'inbox';
  userName: string | null;
  fileName: string;
  libraryId: string | null;
  libraryName: string | null;
  outcome: ImportResult['outcome'] | 'failed';
  reason: string | null;
  report: string | null;
}

const libs = '/tendering/libraries';
export const libraryApi = {
  list: () => request<LibrarySummary[]>(libs),
  get: (id: string) => request<LibraryDetail>(`${libs}/${id}`),
  create: (body: { name: string; description?: string | null }) => request<{ id: string }>(libs, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, body: { name?: string; description?: string | null }) => request<void>(`${libs}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: string) => request<void>(`${libs}/${id}`, { method: 'DELETE' }),
  importGaeb: (body: { fileName: string; contentBase64: string; action: ImportAction; libraryId?: string | null; dryRun?: boolean }) =>
    request<ImportResult>(`${libs}/import-gaeb`, { method: 'POST', body: JSON.stringify(body) }),
  runs: () => request<ImportRun[]>(`${libs}/import-runs`),
  clearWarning: (id: string) => request<void>(`${libs}/${id}/warning`, { method: 'DELETE' }),
  addItem: (id: string, body: LibraryItemInput & { kind: LibraryItem['kind']; shortText: string; afterId?: string | null }) =>
    request<{ id: string }>(`${libs}/${id}/items`, { method: 'POST', body: JSON.stringify(body) }),
  updateItem: (id: string, itemId: string, body: LibraryItemInput) => request<void>(`${libs}/${id}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  removeItem: (id: string, itemId: string) => request<void>(`${libs}/${id}/items/${itemId}`, { method: 'DELETE' }),
};

// --- Projektübergreifende Übersichten ---------------------------------------

export interface TimeOverviewRow {
  projectId: string;
  number: string;
  name: string;
  categories: TimeCategory[];
  uncategorized: { booked: number; mine: number };
  total: number;
  budget: number;
  mine: number;
}

export interface TimeOverview {
  projects: TimeOverviewRow[];
  recent: (TimeEntry & { projectId: string })[];
}

export interface CostOverviewRow {
  projectId: string;
  number: string;
  name: string;
  planned: boolean;
  variants: { id: string; code: string; name: string }[];
  primaryVariantId: string;
  referenceStage: CostStage;
  vatRate: number;
  riskPercent: number;
  rangePercent: number;
  stages: Record<CostStage, { net: number | null; withRisk: number | null }>;
  committed: number;
}

export interface ScheduleOverviewRow {
  projectId: string;
  number: string;
  name: string;
  hasSchedule: boolean;
  stale: boolean;
  start: string | null;
  finish: string | null;
  targetFinish: string | null;
  floatToTarget: number | null;
  durationDays: number | null;
  baseline: { name: string; start: string; finish: string; durationDays: number } | null;
  finishDelta: number | null;
  tasks: { total: number; done: number; running: number; blocked: number; critical: number; overdue: number };
  nextMilestone: { code: string; name: string; date: string } | null;
}

/** Each module lists the projects the caller may see; changes go through the project routes. */
export const overviewApi = {
  time: () => request<TimeOverview>('/projectdata/overview/time'),
  costs: () => request<CostOverviewRow[]>('/costs/overview'),
  schedule: () => request<ScheduleOverviewRow[]>('/scheduling/overview'),
};

// --- Aufgaben und Kalender (aus dem Terminplan) ------------------------------

export interface AgendaTask {
  id: string;
  code: string;
  name: string;
  group: string | null;
  start: string | null;
  finish: string | null;
  durationDays: number;
  milestone: boolean;
  status: TaskStatus;
  owner: string | null;
  position: string | null;
  deliverable: string | null;
  deadline: string | null;
  critical: boolean;
  mine: boolean;
  wbsNodeId: string | null;
  note: string | null;
  /** A start the user pinned down ("frühestens"), else null. */
  startFixed: string | null;
  doneOn: string | null;
}

export interface AgendaProject {
  projectId: string;
  number: string;
  name: string;
  tasks: AgendaTask[];
}

export interface CalendarFeed {
  id: string;
  scope: 'mine' | 'all';
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export const agendaApi = {
  list: () => request<AgendaProject[]>('/scheduling/agenda'),
  feeds: () => request<CalendarFeed[]>('/scheduling/feeds'),
  createFeed: (scope: 'mine' | 'all', label: string) => request<CalendarFeed & { url: string }>('/scheduling/feeds', { method: 'POST', body: JSON.stringify({ scope, label }) }),
  removeFeed: (id: string) => request<void>(`/scheduling/feeds/${id}`, { method: 'DELETE' }),
};

// --- Nextcloud-Projektordner (Freigabelink, nur lesen) ------------------------

export interface CloudFolder {
  configured: boolean;
  shareUrl: string | null;
  hasPassword: boolean;
}

export interface CloudEntry {
  name: string;
  path: string;
  folder: boolean;
  size: number | null;
  modified: string | null;
  contentType: string | null;
  fileId: string | null;
  /** Nextcloud's internal link where known - no share password for anyone logged in to Nextcloud. */
  open: string;
  /** The share view, for people without a Nextcloud login. */
  share: string;
  /** Through PST (path under /api), with the stored password; a folder as ZIP. */
  download: string;
}

export interface CloudListing {
  path: string;
  open: string;
  share: string;
  /** This folder as ZIP, through PST (path under /api). */
  download: string;
  entries: CloudEntry[];
}

const cloud = (projectId: string) => `/projectdata/projects/${projectId}/cloud`;
export const cloudApi = {
  get: (p: string) => request<CloudFolder>(cloud(p)),
  list: (p: string, path: string) => request<CloudListing>(`${cloud(p)}/list?path=${encodeURIComponent(path)}`),
  /** password: undefined keeps the stored one, null removes it. */
  save: (p: string, shareUrl: string, password?: string | null) => request<CloudFolder>(cloud(p), { method: 'PUT', body: JSON.stringify({ shareUrl, password }) }),
  remove: (p: string) => request<void>(cloud(p), { method: 'DELETE' }),
};

// --- Snapshots je Projekt (ADR 0005) ----------------------------------------

export type SnapshotReason = 'open' | 'manual' | 'before-restore' | 'before-rollforward';
export type SnapshotSummary = Record<string, Record<string, number>>;

export interface SnapshotItem {
  id: string;
  createdAt: string;
  reason: SnapshotReason;
  by: string | null;
  summary: SnapshotSummary;
  bytes: number;
  kept: string[];
}

export interface SnapshotOverview {
  /** perPerson: restored per chosen person (hours), never part of "alle wählen". */
  parts: { key: string; label: string; canRestore: boolean; perPerson: boolean }[];
  /** Projektleitung and Administration may delete single snapshots. */
  canDelete: boolean;
  current: SnapshotSummary;
  snapshots: SnapshotItem[];
}

export interface SnapshotPerson {
  /** null: rows without a person ("none" when choosing). */
  id: string | null;
  name: string;
  inSnapshot: number;
  now: number;
  canRestore: boolean;
}

const snaps = (projectId: string) => `/projects/${projectId}/snapshots`;
export const snapshotApi = {
  list: (p: string) => request<SnapshotOverview>(snaps(p)),
  take: (p: string, reason: 'open' | 'manual') => request<{ id: string; created: boolean }>(snaps(p), { method: 'POST', body: JSON.stringify({ reason }) }),
  remove: (p: string, id: string) => request<void>(`${snaps(p)}/${id}`, { method: 'DELETE' }),
  people: (p: string, id: string) => request<Record<string, SnapshotPerson[]>>(`${snaps(p)}/${id}/people`),
  restore: (p: string, id: string, parts: string[], people?: Record<string, string[]>) =>
    request<{ restored: string[]; before: string; people: Record<string, string[]>; state: string }>(`${snaps(p)}/${id}/restore`, { method: 'POST', body: JSON.stringify({ parts, people }) }),
};

/**
 * A write an undo step replays (ui/undo). Plain data rather than a function,
 * so the steps survive reloading the page.
 */
export interface Replay {
  method: 'PATCH' | 'PUT' | 'POST';
  path: string;
  body: Record<string, unknown>;
}

export const replay = (r: Replay): Promise<unknown> => request<unknown>(r.path, { method: r.method, body: JSON.stringify(r.body) });

/** Where the undoable writes go - the same paths as the calls above. */
export const undoPaths = {
  project: (p: string) => `/projects/${p}`,
  costPlan: (p: string) => costs(p),
  costGroup: (p: string, id: string) => `${costs(p)}/groups/${id}`,
  costItem: (p: string, id: string) => `${costs(p)}/items/${id}`,
  costValue: (p: string, id: string) => `${costs(p)}/values/${id}`,
  costVariant: (p: string, id: string) => `${costs(p)}/variants/${id}`,
  schedulePlan: (p: string) => `${scheduling(p)}/plan`,
  task: (p: string, id: string) => `${scheduling(p)}/tasks/${id}`,
  wbs: (p: string, id: string) => `${scheduling(p)}/wbs/${id}`,
  projectDetails: (p: string) => `${projectData(p)}/details`,
  timeEntry: (p: string, id: string) => `${time(p)}/entries/${id}`,
  timeCategory: (p: string, id: string) => `${time(p)}/categories/${id}`,
  restore: (p: string, snapshotId: string) => `${snaps(p)}/${snapshotId}/restore`,
};

// --- Angebote (Modul 5, ADR 0010) ---------------------------------------------

export type { CvLine, OfferAttachments, OfferReference, PartyProfile } from '@pst/shared';
export type { Constellation, OfferParty, OfferStatus, OfferTexts, OfferVariantData, OfferRole, OfferSubcontractor, OfferPosition, OfferExtra, OfferShare, OfferParams } from '@pst/shared';

export interface OfferSnapshot {
  id: string;
  reason: 'open' | 'manual' | 'before-restore';
  label: string;
  createdAt: string;
  createdBy: string | null;
  variants: string[];
}

export interface OfferSummary {
  id: string;
  code: string;
  name: string;
  client: string;
  status: string;
  dueDate: string | null;
  variants: { id: string; code: string; name: string; net: number }[];
  projectId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OfferVariant {
  id: string;
  code: string;
  name: string;
  constellation: OfferConstellation;
  sortOrder: number;
  data: VariantData;
  version: number;
}

export interface Offer {
  id: string;
  code: string;
  name: string;
  client: string;
  lot: string;
  location: string;
  state: string;
  status: string;
  dueDate: string | null;
  offerDate: string | null;
  bindingDays: number | null;
  description: string;
  texts: Texts;
  hoai: HoaiCalculation[];
  feeBasis: FeeBasis;
  projectId: string | null;
  version: number;
  variants: OfferVariant[];
}

export type OfferPatch = Partial<Omit<Offer, 'id' | 'variants' | 'version' | 'projectId' | 'texts'>> & { texts?: Partial<Texts> };

export interface OfferEditor { userId: string; displayName: string; email: string; role: string }

export const offersApi = {
  exportUrl: (id: string) => `/api/offers/${id}/export`,
  duplicate: (id: string, body: { code: string; name: string }) => request<{ id: string; code: string; notes: string[] }>(`/offers/${id}/duplicate`, { method: 'POST', body: JSON.stringify(body) }),
  snapshots: (id: string) => request<OfferSnapshot[]>(`/offers/${id}/snapshots`),
  takeSnapshot: (id: string, reason: 'open' | 'manual', label = '') => request<{ created: boolean; id: string | null }>(`/offers/${id}/snapshots`, { method: 'POST', body: JSON.stringify({ reason, label }) }),
  restoreSnapshot: (id: string, snapshotId: string) => request<{ before: string | null; notes: string[] }>(`/offers/${id}/snapshots/${snapshotId}/restore`, { method: 'POST' }),
  removeAllSnapshots: (id: string) => request<{ removed: number }>(`/offers/${id}/snapshots`, { method: 'DELETE' }),
  removeSnapshot: (id: string, snapshotId: string) => request<void>(`/offers/${id}/snapshots/${snapshotId}`, { method: 'DELETE' }),
  importFile: (file: unknown) => request<{ id: string; code: string; notes: string[] }>('/offers/import', { method: 'POST', body: JSON.stringify(file) }),
  list: () => request<OfferSummary[]>('/offers'),
  get: (id: string) => request<Offer>(`/offers/${id}`),
  create: (body: { code: string; name: string; client?: string; constellation?: OfferConstellation; feeBasis?: FeeBasis }) => request<{ id: string }>('/offers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: string, version: number, patch: OfferPatch) => request<{ version: number }>(`/offers/${id}`, { method: 'PATCH', body: JSON.stringify({ ...patch, version }) }),
  remove: (id: string) => request<void>(`/offers/${id}`, { method: 'DELETE' }),
  addVariant: (id: string, body: { code: string; name?: string; constellation: OfferConstellation; copyFrom?: string | null }) => request<{ id: string }>(`/offers/${id}/variants`, { method: 'POST', body: JSON.stringify(body) }),
  saveVariant: (id: string, variantId: string, version: number, body: { name?: string; constellation?: OfferConstellation; data?: VariantData }) =>
    request<{ version: number }>(`/offers/${id}/variants/${variantId}`, { method: 'PUT', body: JSON.stringify({ ...body, version }) }),
  removeVariant: (id: string, variantId: string) => request<void>(`/offers/${id}/variants/${variantId}`, { method: 'DELETE' }),
  parties: () => request<Party[]>('/offers/parties'),
  addParty: (body: Omit<Party, 'id'>) => request<{ id: string }>('/offers/parties', { method: 'POST', body: JSON.stringify(body) }),
  saveParty: (id: string, body: Omit<Party, 'id'>) => request<void>(`/offers/parties/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeParty: (id: string) => request<void>(`/offers/parties/${id}`, { method: 'DELETE' }),
  editors: () => request<OfferEditor[]>('/offers/editors'),
  members: (id: string) => request<OfferEditor[]>(`/offers/${id}/members`),
  setMembers: (id: string, userIds: string[]) => request<void>(`/offers/${id}/members`, { method: 'PUT', body: JSON.stringify({ userIds }) }),
  references: () => request<Ref[]>('/offers/references'),
  addReference: (body: Omit<Ref, 'id'>) => request<{ id: string }>('/offers/references', { method: 'POST', body: JSON.stringify(body) }),
  saveReference: (id: string, body: Omit<Ref, 'id'>) => request<void>(`/offers/references/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeReference: (id: string) => request<void>(`/offers/references/${id}`, { method: 'DELETE' }),
};

// --- Vorlagen (Einstellungen, AP-91) ---------------------------------------------

export interface TemplateVersion {
  id: string;
  kind: string;
  fileName: string;
  version: number;
  sha256: string;
  bytes: number;
  check: { ok: boolean; problems: string[]; notes: string[] };
  active: boolean;
  note: string;
  createdAt: string;
}

export interface TemplateKind {
  kind: string;
  label: string;
  description: string;
  extension: string;
  versions: TemplateVersion[];
}

const toBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};

export const templatesApi = {
  overview: () => request<TemplateKind[]>('/settings/templates'),
  upload: async (kind: string, file: File, activate: boolean) => request<TemplateVersion>('/settings/templates', { method: 'POST', body: JSON.stringify({ kind, fileName: file.name, data: await toBase64(file), activate }) }),
  activate: (id: string) => request<void>(`/settings/templates/${id}/active`, { method: 'PUT' }),
  remove: (id: string) => request<void>(`/settings/templates/${id}`, { method: 'DELETE' }),
  fileUrl: (id: string) => `/api/settings/templates/${id}/file`,
};

// --- Angebotsunterlagen (AP-93, AP-94) --------------------------------------------

export interface OfferCheck { name: string; ok: boolean; detail: string }
export interface OfferPackage {
  id: string;
  fileName: string;
  bytes: number;
  createdAt: string;
  report: { ok?: boolean; checks?: OfferCheck[]; files?: string[]; open?: Record<string, string[]>; templates?: { calculation: number; document: number } };
}

export const offerFilesApi = {
  list: (offerId: string) => request<OfferPackage[]>(`/offers/${offerId}/files`),
  generate: (offerId: string) => request<OfferPackage>(`/offers/${offerId}/files`, { method: 'POST' }),
  url: (offerId: string, packageId: string) => `/api/offers/${offerId}/files/${packageId}`,
};

// --- KI-Auswertung (AP-97, ADR 0011) ---------------------------------------------

export type { AiFinding, AiStation, OfferAiRun, OfferAnalysis, OfferDocument, OfferDraft } from '@pst/shared';
import type { OfferAiRun as AiRun, OfferDocument as OfferDoc } from '@pst/shared';

export interface AiSettingsStatus {
  available: boolean;
  worker: { at: string; state: string; job: string | null; claude: string | null } | null;
  folder: { path: string; writable: boolean };
  settings: AiSettings;
  api: { keySet: boolean; today: number; maxPerDay: number };
  reason: string | null;
}

/** Claude Code on the server (ADR 0011) or the Claude API (ADR 0022). */
export type AiBackend = 'server' | 'api';
export interface AiSettings { model: string; effort: string; backend: AiBackend }

/** UTF-8 text as base64, in pieces so a long text does not overflow the call stack. */
function textBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let s = '';
  for (let i = 0; i < bytes.length; i += 8192) s += String.fromCharCode(...bytes.subarray(i, i + 8192));
  return btoa(s);
}

/** The office's colours (Einstellungen › Darstellung); reading needs no login. */
export const themeApi = {
  get: () => request<ThemeColors & { custom: boolean }>('/settings/theme'),
  save: (colors: ThemeColors) => request<void>('/settings/theme', { method: 'PUT', body: JSON.stringify(colors) }),
  reset: () => request<void>('/settings/theme', { method: 'DELETE' }),
};

export type ConnectionGroup = 'mail' | 'genesis' | 'anthropic' | 'ntfy';

/** Einstellungen › Verbindungen: secrets go in, only "set or not" comes back. */
export const connectionsApi = {
  get: () => request<ConnectionsView>('/settings/connections'),
  saveMail: (input: MailInput) => request<void>('/settings/connections/mail', { method: 'PUT', body: JSON.stringify(input) }),
  saveGenesis: (input: GenesisInput) => request<void>('/settings/connections/genesis', { method: 'PUT', body: JSON.stringify(input) }),
  saveAnthropic: (key: string) => request<void>('/settings/connections/anthropic', { method: 'PUT', body: JSON.stringify({ key }) }),
  reset: (group: ConnectionGroup) => request<void>(`/settings/connections/${group}`, { method: 'DELETE' }),
  test: (group: ConnectionGroup) => request<{ ok: boolean; message: string }>(`/settings/connections/${group}/test`, { method: 'POST' }),
  saveNtfy: (input: NtfyInput) => request<void>('/settings/connections/ntfy', { method: 'PUT', body: JSON.stringify(input) }),
  savePortal: (id: PortalId, input: PortalInput) => request<void>(`/settings/connections/portals/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  probePortal: (id: PortalId) => request<void>(`/settings/connections/portals/${id}/probe`, { method: 'POST' }),
  releasePortal: (id: PortalId, note: string) => request<void>(`/settings/connections/portals/${id}/release`, { method: 'POST', body: JSON.stringify({ note }) }),
  removePortal: (id: PortalId) => request<void>(`/settings/connections/portals/${id}`, { method: 'DELETE' }),
};

/** Fetch jobs for the portals (ADR 0023): what the server's worker did and does. */
export const portalJobsApi = {
  overview: () => request<PortalJobsOverview>('/portal-jobs'),
  cancel: (id: string) => request<void>(`/portal-jobs/${id}/cancel`, { method: 'POST' }),
  create: (input: { portal: PortalId; kind: 'einzel' | 'auftrag'; target: string }) => request<{ id: string }>('/portal-jobs', { method: 'POST', body: JSON.stringify(input) }),
  /** The night round (PLAN-PORTALE.md 3.4b): switch it, end the ramp week after the report. */
  setNight: (portal: PortalId, enabled: boolean) => request<void>(`/portal-jobs/rounds/${portal}/night`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
  confirmRamp: (portal: PortalId) => request<void>(`/portal-jobs/rounds/${portal}/confirm-ramp`, { method: 'POST' }),
};

/** A price from the Baupreislexikon (ADR 0023), with fetch date and price date. */
export interface LexiconPrice {
  id: string;
  key: string;
  label: string;
  unit: string | null;
  low: number | null;
  mid: number | null;
  high: number | null;
  region: string | null;
  priceDate: string | null;
  costGroup: string | null;
  fetchedAt: string;
}

export const lexiconApi = {
  list: (q: string, offset: number) => request<{ total: number; rows: LexiconPrice[] }>(`/costs/lexicon?${new URLSearchParams({ q, offset: String(offset) }).toString()}`),
};

export const aiSettingsApi = {
  status: () => request<AiSettingsStatus>('/settings/ai'),
  save: (input: AiSettings) => request<void>('/settings/ai', { method: 'PUT', body: JSON.stringify(input) }),
};

export const offerAiApi = {
  status: () => request<{ available: boolean; reason: string | null; model: string; effort: string }>('/offers/ai-status'),
  documents: (offerId: string) => request<OfferDoc[]>(`/offers/${offerId}/documents`),
  addDocument: async (offerId: string, file: File) => request<OfferDoc>(`/offers/${offerId}/documents`, { method: 'POST', body: JSON.stringify({ fileName: file.name, data: await toBase64(file) }) }),
  addText: (offerId: string, fileName: string, text: string) => request<OfferDoc>(`/offers/${offerId}/documents`, { method: 'POST', body: JSON.stringify({ fileName, data: textBase64(text) }) }),
  documentUrl: (offerId: string, id: string) => `/api/offers/${offerId}/documents/${id}`,
  removeDocument: (offerId: string, id: string) => request<void>(`/offers/${offerId}/documents/${id}`, { method: 'DELETE' }),
  runs: (offerId: string) => request<AiRun[]>(`/offers/${offerId}/ai`),
  analyse: (offerId: string, note: string) => request<{ id: string }>(`/offers/${offerId}/ai/analyse`, { method: 'POST', body: JSON.stringify({ note }) }),
  draft: (offerId: string, input: { basedOn: string; variantId: string; note: string }) => request<{ id: string }>(`/offers/${offerId}/ai/draft`, { method: 'POST', body: JSON.stringify(input) }),
  apply: (offerId: string, runId: string, station: 'analyse' | 'entwurf') => request<{ dropped: string[] }>(`/offers/${offerId}/ai/${runId}/apply`, { method: 'POST', body: JSON.stringify({ station }) }),
  removeRun: (offerId: string, runId: string) => request<void>(`/offers/${offerId}/ai/${runId}`, { method: 'DELETE' }),
};

// --- Projekt aus Angebot (AP-98) --------------------------------------------------

export type { ProjectFromOfferOptions, ProjectPart, ProjectProposal } from '@pst/shared';
import type { ProjectFromOfferOptions as ProjectOptions, ProjectPart as Part, ProjectProposal as Proposal } from '@pst/shared';

export interface ProjectFromOfferResult {
  projectId: string;
  report: { name: string; ok: boolean; detail: string | null }[];
  notes: string[];
}

export const offerProjectApi = {
  proposal: (offerId: string, variantId: string, options: Omit<ProjectOptions, 'bundesland'>) =>
    request<Proposal>(`/offers/${offerId}/project-proposal`, { method: 'POST', body: JSON.stringify({ variantId, options }) }),
  create: (offerId: string, body: { number: string; name: string; variantId: string; parts: Part[]; options: Omit<ProjectOptions, 'bundesland'> }) =>
    request<ProjectFromOfferResult>(`/offers/${offerId}/project`, { method: 'POST', body: JSON.stringify(body) }),
};
